import { Injectable } from '@angular/core';
import { BaseService } from 'cre-ng';
import { map } from 'rxjs/operators';

import {
  ApiJob,
  ApiJobById,
  Division,
  DomicileSmall,
  Job,
  JobFilter,
} from '@app/shared/shared.models';

@Injectable()
export class JobsService extends BaseService {
  divisions(): Promise<Division[]> {
    return this.httpClient.get<Division[]>(this.serviceUrl + '/divisions').toPromise();
  }

  featuredJobs(): Promise<Job[]> {
    return this.httpClient
      .get<ApiJob[]>(this.serviceUrl + '/featuredjobs')
      .pipe(
        map((response: ApiJob[]) => {
          const jobs: Job[] = [];
          response.forEach((apiJob) => {
            const newObject = {
              city: '',
              description: apiJob.LaneDetails,
              division: this.mapDivision(apiJob.Division),
              domicileId: -1,
              driverType: this.mapDriverType(apiJob.DriverType),
              experience: this.mapExperience(apiJob.Experience),
              hideWage: apiJob.AverageWeekly === -1 || apiJob.AverageAnnualized === -1,
              hideWageYear: apiJob.TopWeekly === -1 || apiJob.TopAnnualized === -1,
              homeTime: apiJob.HometimeLabel,
              jobId: apiJob.JobID.toString(),
              state: '',
              title: apiJob.Title === '' ? 'Truck Driver' : apiJob.Title,
              wageAverageAnnualized: this.formatAnnualSalary(apiJob.AverageAnnualized),
              wageAverageWeekly: this.formatSalary(apiJob.AverageWeekly),
              wageTopAnnualized: this.formatAnnualSalary(apiJob.TopAnnualized),
              wageTopWeekly: this.formatSalary(apiJob.TopWeekly),
            } as Job;
            jobs.push(newObject);
          });
          return jobs;
        })
      )
      .toPromise();
  }

  getEndPoint(): string {
    return '/jobpositions/api/v1';
  }

  jobById(id: number, city: string, state: string): Promise<Job> {
    return this.httpClient
      .get<ApiJobById>(this.serviceUrl + `/jobs/${id}/details`)
      .pipe(
        map((response: ApiJobById) => {
          let domicileId = -1;
          const domiciles: DomicileSmall[] = [];
          response.Domiciles.forEach((e) => {
            const domicile = {
              city: this.capitalize(e.City),
              state: e.StateCode,
            } as DomicileSmall;
            domiciles.push(domicile);
            if (e.City === city && e.State === state) {
              domicileId = e.JobDomicileId;
              return;
            }
          });
          const newObject = {
            city,
            description: response.LaneDetails,
            division: this.mapDivision(response.Division),
            domicileId,
            domiciles,
            driverType: this.mapDriverType(response.DriverType),
            experience: this.mapExperience(response.Experience),
            hideWage: response.AverageWeekly === -1 || response.AverageAnnualized === -1,
            hideWageYear: response.TopWeekly === -1 || response.TopAnnualized === -1,
            homeTime: response.HometimeLabel,
            jobId: response.JobID.toString(),
            state,
            title: response.Title === '' ? 'Truck Driver' : response.Title,
            wageAverageAnnualized: this.formatAnnualSalary(response.AverageAnnualized),
            wageAverageWeekly: this.formatSalary(response.AverageWeekly),
            wageTopAnnualized: this.formatAnnualSalary(response.TopAnnualized),
            wageTopWeekly: this.formatSalary(response.TopWeekly),
          } as Job;
          return newObject;
        })
      )
      .toPromise();
  }

  jobsFilter(filter: JobFilter): Promise<Job[]> {
    return this.httpClient
      .post<ApiJob[]>(this.serviceUrl + '/jobsfilter', filter)
      .pipe(
        map((response: ApiJob[]) => {
          const jobs: Job[] = [];
          response.forEach((apiJob) => {
            const newObject = {
              city: this.capitalize(apiJob.Domiciles.City),
              description: apiJob.LaneDetails,
              division: this.mapDivision(apiJob.Division),
              domicileId: -1,
              driverType: this.mapDriverType(apiJob.DriverType),
              experience: this.mapExperience(apiJob.Experience),
              hideWage: apiJob.AverageWeekly === -1 || apiJob.AverageAnnualized === -1,
              hideWageYear: apiJob.TopWeekly === -1 || apiJob.TopAnnualized === -1,
              homeTime: apiJob.HometimeLabel,
              jobId: apiJob.JobID.toString(),
              state: this.capitalize(apiJob.Domiciles.State),
              title: apiJob.Title === '' ? 'Truck Driver' : apiJob.Title,
              wageAverageAnnualized: this.formatAnnualSalary(apiJob.AverageAnnualized),
              wageAverageWeekly: this.formatSalary(apiJob.AverageWeekly),
              wageTopAnnualized: this.formatAnnualSalary(apiJob.TopAnnualized),
              wageTopWeekly: this.formatSalary(apiJob.TopWeekly),
            } as Job;
            jobs.push(newObject);
          });
          return jobs;
        })
      )
      .toPromise();
  }

  private capitalize(input: string): string {
    return input
      .toLowerCase()
      .split(' ')
      .map((s) => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }

  private formatAnnualSalary(rawMoney: number): string {
    const moneyFormatted = this.formatSalary(rawMoney);
    const indexOfComma = moneyFormatted.indexOf(',');
    if (indexOfComma > 0) {
      return moneyFormatted.slice(0, indexOfComma) + 'k';
    }
    return moneyFormatted;
  }

  private formatSalary(rawMoney: number): string {
    const moneyFormatted = rawMoney.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
    if (moneyFormatted.length > 3) {
      return moneyFormatted.slice(0, -3);
    }
    return moneyFormatted;
  }

  private mapDivision(rawDivision: string): string {
    switch (rawDivision) {
      case 'DEDICT':
        return 'Dedicated';
      case 'OVER_THE_ROAD':
        return 'Over the Road';
      case 'MEXICO':
        return 'Mexico';
      case 'INTMDL':
        return 'Intermodal';
      case 'SCHL':
        return 'Instructor';
      case 'OTHER':
        return 'Other';
      default:
        return '';
    }
  }

  private mapDriverType(rawDriverType: string): string {
    switch (rawDriverType) {
      case 'TEAM':
        return 'Team';
      case 'SOLO':
        return 'Solo';
      case 'TRAIN':
        return 'Trainer';
      default:
        return '';
    }
  }

  private mapExperience(rawExperience: number): string {
    switch (rawExperience) {
      case -1:
        return '';
      case 0:
        return 'Need CDL';
      case 1:
      case 2:
        return 'Less than 3 months';
      default:
        return 'More than 3 months';
    }
  }
}
